import React from 'react';
import { cn } from '../../utils/cn.ts';
import { Icon } from './Icon.tsx';
import { IconButton } from './IconButton.tsx';

interface InputRightAction {
    icon: string;
    label: string;
    onClick: () => void;
    disabled?: boolean;
}

interface InputProps extends React.InputHTMLAttributes<HTMLInputElement> {
    label?: string;
    error?: string;
    leftIcon?: string;
    rightIcon?: string;
    inputClassName?: string;
    /** Inline text prefix (e.g. "$"). Takes precedence over leftIcon. */
    prefix?: string;
    /** Inline text suffix (e.g. "kg"). Takes precedence over rightIcon. */
    suffix?: string;
    fullWidth?: boolean;
    helperText?: string;
    rightAction?: InputRightAction;
    ref?: React.Ref<HTMLInputElement>;
}

const NativeInput = 'input';

export const Input: React.FC<InputProps> = ({
    label,
    error,
    leftIcon,
    rightIcon,
    prefix,
    suffix,
    fullWidth = true,
    helperText,
    inputClassName,
    rightAction,
    className,
    disabled,
    id,
    ref,
    ...props
}) => {
    const generatedId = React.useId();
    const inputId = id ?? generatedId;
    const hasLeftAdornment = prefix || leftIcon;
    const hasRightAdornment = suffix || rightIcon || rightAction;
    return (
        <div className={cn(fullWidth && 'w-full', className)}>
            {label ? <label
                    htmlFor={inputId}
                    className="mb-1 block font-medium text-gray-700 text-sm dark:text-gray-300"
                >
                    {label}
                </label> : null}
            
            <div className="relative">
                {prefix ? (
                    <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3">
                        <span className="text-gray-500 text-sm dark:text-gray-400">{prefix}</span>
                    </div>
                ) : leftIcon ? (
                    <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3">
                        <Icon name={leftIcon} size="sm" className="text-gray-400" />
                    </div>
                ) : null}
                
                <NativeInput
                    ref={ref}
                    id={inputId}
                    disabled={disabled}
                    className={cn(
                        'block w-full rounded-lg border',
                        'bg-white dark:bg-surface-dark',
                        'text-gray-900 dark:text-white',
                        'placeholder-gray-400 dark:placeholder-gray-500',
                        'focus-ring transition-colors',
                        hasLeftAdornment ? (prefix ? 'pl-8' : 'pl-9') : 'pl-3',
                        hasRightAdornment ? 'pr-9' : 'pr-3',
                        'py-2 text-sm',
                        error 
                            ? 'border-red-500 focus:ring-red-500' 
                            : 'border-gray-300 hover:border-gray-400 dark:border-border-dark dark:hover:border-gray-600',
                        disabled && 'cursor-not-allowed bg-gray-50 opacity-60 dark:bg-gray-800',
                        inputClassName
                    )}
                    {...props}
                />

                {suffix ? (
                    <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-3">
                        <span className="text-gray-500 text-sm dark:text-gray-400">{suffix}</span>
                    </div>
                ) : rightAction ? (
                    <div className="absolute inset-y-0 right-1 flex items-center">
                        <IconButton
                            disabled={disabled || rightAction.disabled}
                            icon={rightAction.icon}
                            label={rightAction.label}
                            onClick={rightAction.onClick}
                            size="sm"
                            variant="ghost"
                        />
                    </div>
                ) : rightIcon ? (
                    <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-3">
                        <Icon 
                            name={rightIcon} 
                            size="sm" 
                            className={cn(error ? 'text-red-500' : 'text-gray-400')}
                        />
                    </div>
                ) : null}
            </div>

            {(error || helperText) ? <p className={cn('mt-1 text-xs', error ? 'text-red-500' : 'text-gray-500 dark:text-gray-400')}>
                    {error || helperText}
                </p> : null}
        </div>
    );
};

Input.displayName = 'Input';














